import React, { useState, useEffect } from 'react';
import { Download, Edit2, Trash2, Save, X, Lock, Eye, EyeOff, ArrowLeft, Plus } from 'lucide-react';
import productsData from './data/latestProducts';

const AdminConsole = ({ onExit }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [products, setProducts] = useState({});
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [isNewProduct, setIsNewProduct] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBrand, setFilterBrand] = useState('all');

  // Simple password protection - you can change this
  const ADMIN_PASSWORD = 'apf';

  useEffect(() => {
    if (authenticated) {
      // Deep clone the products data
      setProducts(JSON.parse(JSON.stringify(productsData)));
    }
  }, [authenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
    } else {
      alert('Incorrect password');
    }
  };

  const downloadJSON = () => {
    const dataStr = JSON.stringify(products, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    // Format: products-YYYY-MM-DD-HHMMSS.json
    const timestamp = new Date().toISOString()
      .replace(/T/, '-')
      .replace(/:/g, '')
      .replace(/\..+/, '')
      .substring(0, 17); // YYYY-MM-DD-HHMMSS
    link.download = `products-${timestamp}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getAllProducts = () => {
    const allProducts = [];
    Object.keys(products).forEach(brand => {
      if (products[brand].products) {
        Object.keys(products[brand].products).forEach(key => {
          allProducts.push({
            brand,
            key,
            data: products[brand].products[key]
          });
        });
      }
    });
    return allProducts;
  };

  const getFilteredProducts = () => {
    let filtered = getAllProducts();
    
    if (filterBrand !== 'all') {
      filtered = filtered.filter(p => p.brand === filterBrand);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.data.product_name?.toLowerCase().includes(term) ||
        p.brand?.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  };

  const handleEdit = (brand, key) => {
    setSelectedBrand(brand);
    setSelectedProduct({ key, data: JSON.parse(JSON.stringify(products[brand].products[key])) });
    setIsNewProduct(false);
    setEditMode(true);
  };

  const handleNewProduct = () => {
    const brands = Object.keys(products);
    const defaultBrand = brands[0] || 'Air Doctor';
    
    // Create a template for a new product
    const newProductTemplate = {
      product_name: "New Product",
      brand: null,
      specs: {
        "Amazon Link": "",
        "Price (without discounts)": 0,
        "Noise Levels": "",
        "Size": "",
        "Weight": "",
        "CADR (Smoke)": "",
        "CADR (Dust)": "",
        "CADR (Pollen)": "",
        "Coverage": 0,
        "Energy Star Certification": "no",
        "Filter Types": "",
        "Fan Speeds": "",
        "App / Smart Features": "No",
        "Additional Certifications": "",
        "Energy Consumption": "",
        "Warranty": "",
        "AAFA Certification": "no",
        "HSA/FSA Eligibility": "no"
      },
      concerns: {
        allergens_pollen: false,
        dust: false,
        smoke: false,
        odors: false,
        VOC_chemicals: false,
        pet_dander: false,
        germs_bacteria_viruses: false,
        mold: false
      },
      important_features: {
        smart_features: {
          wifi: false,
          app_control: false,
          bluetooth: false,
          voice_control: false
        },
        smart_capabilities: {
          auto_mode: false,
          air_quality_sensor: false
        },
        energy_star_certified: false,
        hsa_fsa_eligible: false,
        quiet_operation: false,
        maintenance: {
          washable_pre_filter: false,
          filter_change_indicator: false,
          long_filter_life: false,
          estimated_filter_life_months: null,
          easy_maintenance: false
        }
      }
    };
    
    // Generate a new key (use timestamp or next number)
    const existingKeys = products[defaultBrand]?.products ? Object.keys(products[defaultBrand].products) : [];
    const newKey = existingKeys.length.toString();
    
    setSelectedBrand(defaultBrand);
    setSelectedProduct({ key: newKey, data: newProductTemplate });
    setIsNewProduct(true);
    setEditMode(true);
  };

  const handleDelete = (brand, key) => {
    if (window.confirm(`Delete ${products[brand].products[key].product_name}?`)) {
      const newProducts = { ...products };
      delete newProducts[brand].products[key];
      setProducts(newProducts);
    }
  };

  const handleSave = () => {
    const newProducts = { ...products };
    
    if (isNewProduct) {
      // For new products, ensure the brand exists
      if (!newProducts[selectedBrand]) {
        newProducts[selectedBrand] = {
          manufacturer: selectedBrand,
          products: {}
        };
      }
      if (!newProducts[selectedBrand].products) {
        newProducts[selectedBrand].products = {};
      }
      
      // Find the next available key
      const existingKeys = Object.keys(newProducts[selectedBrand].products);
      const numericKeys = existingKeys.map(k => parseInt(k)).filter(k => !isNaN(k));
      const nextKey = numericKeys.length > 0 ? Math.max(...numericKeys) + 1 : 0;
      
      newProducts[selectedBrand].products[nextKey.toString()] = selectedProduct.data;
    } else {
      // Update existing product
      newProducts[selectedBrand].products[selectedProduct.key] = selectedProduct.data;
    }
    
    setProducts(newProducts);
    setEditMode(false);
    setSelectedProduct(null);
    setIsNewProduct(false);
  };

  const handleCancel = () => {
    setEditMode(false);
    setSelectedProduct(null);
    setIsNewProduct(false);
  };

  const updateField = (path, value) => {
    const newProduct = { ...selectedProduct };
    const keys = path.split('.');
    let current = newProduct.data;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    setSelectedProduct(newProduct);
  };

  // Login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="flex items-center justify-center mb-6">
            <Lock className="w-12 h-12 text-blue-700" />
          </div>
          <h1 className="text-2xl font-bold text-center text-gray-800 mb-6">Admin Console</h1>
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                  placeholder="Enter admin password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              className="w-full bg-blue-700 text-white py-3 rounded-lg font-medium hover:bg-blue-800 transition-colors"
            >
              Login
            </button>
          </form>
          <button
            onClick={onExit}
            className="w-full mt-4 text-gray-600 hover:text-gray-800 text-sm"
          >
            Back to App
          </button>
        </div>
      </div>
    );
  }

  // Edit mode screen
  if (editMode && selectedProduct) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {isNewProduct ? 'Create New Product' : `Edit Product: ${selectedProduct.data.product_name}`}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  <Save className="w-4 h-4" /> {isNewProduct ? 'Create' : 'Save'}
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  <X className="w-4 h-4" /> Cancel
                </button>
              </div>
            </div>

            {/* Basic Info */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Basic Information</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                  <input
                    type="text"
                    value={selectedProduct.data.product_name || ''}
                    onChange={(e) => updateField('product_name', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                  {isNewProduct ? (
                    <select
                      value={selectedBrand}
                      onChange={(e) => setSelectedBrand(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    >
                      {Object.keys(products).map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={selectedBrand}
                      disabled
                      className="w-full p-2 border border-gray-300 rounded-lg bg-gray-100"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Specs */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Specifications</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amazon Link</label>
                  <input
                    type="url"
                    value={selectedProduct.data.specs?.["Amazon Link"] || ''}
                    onChange={(e) => updateField('specs.Amazon Link', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (without discounts)</label>
                  <input
                    type="number"
                    value={selectedProduct.data.specs?.["Price (without discounts)"] || ''}
                    onChange={(e) => updateField('specs.Price (without discounts)', parseInt(e.target.value))}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coverage (sq ft)</label>
                  <input
                    type="number"
                    value={selectedProduct.data.specs?.Coverage || ''}
                    onChange={(e) => updateField('specs.Coverage', parseInt(e.target.value))}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Noise Levels</label>
                  <input
                    type="text"
                    value={selectedProduct.data.specs?.["Noise Levels"] || ''}
                    onChange={(e) => updateField('specs.Noise Levels', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Energy Consumption</label>
                  <input
                    type="text"
                    value={selectedProduct.data.specs?.["Energy Consumption"] || ''}
                    onChange={(e) => updateField('specs.Energy Consumption', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>

            {/* Concerns - Checkboxes */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Concerns Addressed</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {Object.keys(selectedProduct.data.concerns || {}).map(concern => (
                  <label key={concern} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedProduct.data.concerns[concern]}
                      onChange={(e) => updateField(`concerns.${concern}`, e.target.checked)}
                      className="w-4 h-4 text-blue-700 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{concern.replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Important Features */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Important Features</h3>
              
              {/* Smart Features */}
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-700 mb-2">Smart Features</h4>
                <div className="grid md:grid-cols-2 gap-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedProduct.data.important_features?.smart_features?.wifi || false}
                      onChange={(e) => updateField('important_features.smart_features.wifi', e.target.checked)}
                      className="w-4 h-4 text-blue-700 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">WiFi</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedProduct.data.important_features?.smart_features?.app_control || false}
                      onChange={(e) => updateField('important_features.smart_features.app_control', e.target.checked)}
                      className="w-4 h-4 text-blue-700 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">App Control</span>
                  </label>
                </div>
              </div>

              {/* Other Features */}
              <div className="grid md:grid-cols-2 gap-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedProduct.data.important_features?.quiet_operation || false}
                    onChange={(e) => updateField('important_features.quiet_operation', e.target.checked)}
                    className="w-4 h-4 text-blue-700 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Quiet Operation</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedProduct.data.important_features?.energy_star_certified || false}
                    onChange={(e) => updateField('important_features.energy_star_certified', e.target.checked)}
                    className="w-4 h-4 text-blue-700 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Energy Star Certified</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedProduct.data.important_features?.maintenance?.easy_maintenance || false}
                    onChange={(e) => updateField('important_features.maintenance.easy_maintenance', e.target.checked)}
                    className="w-4 h-4 text-blue-700 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Easy Maintenance</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main admin screen
  const filteredProducts = getFilteredProducts();
  const brands = Object.keys(products);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onExit}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to App
              </button>
              <h1 className="text-2xl font-bold text-gray-800">Product Admin Console</h1>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleNewProduct}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <Plus className="w-4 h-4" />
                New Product
              </button>
              <button
                onClick={downloadJSON}
                className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800"
              >
                <Download className="w-4 h-4" />
                Download JSON
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-blue-700">{brands.length}</div>
            <div className="text-sm text-gray-600">Total Brands</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-green-700">{getAllProducts().length}</div>
            <div className="text-sm text-gray-600">Total Products</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-purple-700">{filteredProducts.length}</div>
            <div className="text-sm text-gray-600">Filtered Results</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Products</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or brand..."
                className="w-full p-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Brand</label>
              <select
                value={filterBrand}
                onChange={(e) => setFilterBrand(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg"
              >
                <option value="all">All Brands</option>
                {brands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Product Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Brand</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Price</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Coverage</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredProducts.map((product, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-800">{product.data.product_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{product.brand}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      ${product.data.specs?.["Price (without discounts)"] || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {product.data.specs?.Coverage || 'N/A'} sq ft
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(product.brand, product.key)}
                          className="p-1 text-blue-700 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.brand, product.key)}
                          className="p-1 text-red-700 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminConsole;

